/**
 * BuyProductsTab — "Buy Products" CTA modal body for a property card.
 *
 * Lists every enabled, paid product as a visual card gallery (matching
 * the public Shop page's Products module look). Already-bought products
 * stay visible with a "Purchased" ribbon instead of being hidden, so
 * agents can see at a glance what they've already bought for this
 * property. Buying is instant via a Stripe Checkout redirect — customer
 * details on the order are auto-filled from the agent's own profile.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { Loader2, ShoppingCart, CreditCard, X, CheckCircle2 } from "lucide-react";
import { ProductBadge } from "@/components/ProductBadge";
import RichHtml from "@/components/ui/RichHtml";
import BillingIntervalToggle from "@/components/BillingIntervalToggle";
import { defaultInterval, formatIntervalPrice, symbolFor } from "@/utils/priceIntervals";

const API = getApiUrl();

// Diagonal corner ribbon for already-purchased products — mirrors the
// "Featured" ribbon used on public property cards, in the emerald
// palette used site-wide for "paid" / "in basket" states.
const PurchasedRibbon = ({ date }) => (
  <div
    className="pointer-events-none absolute bottom-0 right-0 w-16 h-16 overflow-hidden z-20"
    data-testid="purchased-ribbon"
    aria-label={date ? `Purchased ${date}` : "Already purchased"}
  >
    <div className="absolute bottom-[14px] -right-[24px] -rotate-45 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-7 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-center shadow-md">
      Purchased
    </div>
  </div>
);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export default function BuyProductsTab({ propertyId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [detail, setDetail] = useState(null); // product whose details popup is open

  useEffect(() => {
    if (!propertyId) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/property-admin/products/available/${propertyId}`, { withCredentials: true })
      .then(({ data }) => { if (alive) setProducts(data.products || []); })
      .catch((e) => {
        if (!alive) return;
        toast.error(e?.response?.data?.detail || "Failed to load products");
        setProducts([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [propertyId]);

  const buyNow = async (productId, interval = "one_off") => {
    setBuyingId(productId);
    try {
      const { data } = await axios.post(
        `${API}/property-admin/products/buy`,
        { property_id: propertyId, product_id: productId, origin_url: window.location.origin, billing_interval: interval },
        { withCredentials: true },
      );
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBuyingId(null);
    }
  };

  return (
    <div data-testid="buy-products-tab-content">
      <p className="text-sm text-muted-foreground mb-4">
        Buy any of these services for this property — payment is handled securely via Stripe.
        Products you've already bought stay listed with a "Purchased" ribbon.
      </p>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : products.length === 0 ? (
        <div
          className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground"
          data-testid="buy-products-empty"
        >
          {propertyId
            ? "No products available yet."
            : "No property selected — close this and try again."}
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          data-testid="buy-products-list"
        >
          {products.map((p) => (
            <BuyProductCard
              key={p.product_id}
              p={p}
              isBuying={buyingId === p.product_id}
              buyNow={buyNow}
              onOpenDetail={() => setDetail(p)}
              fmtDate={fmtDate}
            />
          ))}
        </div>
      )}
      {detail && (
        <ProductDetailPopup
          product={detail}
          isBuying={buyingId === detail.product_id}
          fmtDate={fmtDate}
          onClose={() => setDetail(null)}
          onBuy={(interval) => buyNow(detail.product_id, interval)}
        />
      )}
    </div>
  );
}

function BuyProductCard({ p, isBuying, buyNow, onOpenDetail, fmtDate }) {
  const [billingInterval, setBillingInterval] = useState(() => defaultInterval(p));
  const src = p.image_url || "";
  const fitClass = p.image_fit === "cover" ? "object-cover" : "object-contain";
  return (
    <div
      className={`border rounded overflow-hidden bg-background flex flex-col cursor-pointer transition-all hover:shadow-md ${
        p.purchased ? "border-emerald-200 bg-emerald-50/30" : "border-border"
      }`}
      onClick={onOpenDetail}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenDetail(); } }}
      title="Click for more info"
      data-testid={`buy-product-row-${p.product_id}`}
    >
      <div className="relative h-36 bg-muted/40 flex items-center justify-center overflow-hidden">
        {src ? (
          <img src={src} alt={p.name} className={`w-full h-full ${fitClass}`} />
        ) : (
          <ShoppingCart className="h-8 w-8 text-muted-foreground/40" />
        )}
        {p.badge_text && (
          <ProductBadge
            text={p.badge_text}
            position={p.badge_position}
            bgColor={p.badge_bg_color}
            textColor={p.badge_text_color}
            fontFamily={p.badge_font_family}
            fontSize={p.badge_font_size}
            rotation={p.badge_rotation}
            testId={`buy-product-badge-${p.product_id}`}
          />
        )}
        {p.purchased && <PurchasedRibbon date={fmtDate(p.purchased_at)} />}
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div className="font-semibold text-sm leading-snug line-clamp-2">{p.name}</div>
        {p.short_description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{p.short_description}</p>
        )}
        {p.supplier_name && (
          <div className="text-[11px] text-muted-foreground">Supplied by {p.supplier_name}</div>
        )}
        {!p.purchased && (
          <div onClick={(e) => e.stopPropagation()}>
            <BillingIntervalToggle product={p} value={billingInterval} onChange={setBillingInterval} testIdPrefix="buy-product-interval" />
          </div>
        )}
        <div className={`mt-auto flex gap-2 pt-2 ${p.purchased ? "flex-col items-start" : "items-center justify-between"}`}>
          <div
            className="font-serif text-base whitespace-nowrap"
            data-testid={`buy-product-price-${p.product_id}`}
          >
            {formatIntervalPrice(p, billingInterval)}
          </div>
          {p.purchased ? (
            <span
              className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 bg-emerald-100 text-emerald-700 rounded whitespace-nowrap"
              title={p.purchased_at ? `Bought ${fmtDate(p.purchased_at)}` : undefined}
              data-testid={`buy-product-purchased-${p.product_id}`}
            >
              <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
              {p.purchased_at ? `Purchased ${fmtDate(p.purchased_at)}` : "Purchased"}
            </span>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); buyNow(p.product_id, billingInterval); }}
              disabled={isBuying}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 bg-primary text-primary-foreground hover:opacity-90 transition-colors disabled:opacity-50 whitespace-nowrap rounded"
              data-testid={`buy-product-buy-btn-${p.product_id}`}
            >
              {isBuying ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CreditCard className="h-3.5 w-3.5" />
              )}
              Buy Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductDetailPopup({ product, isBuying, fmtDate, onClose, onBuy }) {
  useLockBodyScroll(true);
  const src = product.image_url || "";
  const fitClass = product.image_fit === "cover" ? "object-cover" : "object-contain";
  const [billingInterval, setBillingInterval] = useState(() => defaultInterval(product));
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid={`buy-product-detail-${product.product_id}`}
    >
      <div
        className="bg-background max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-serif text-xl" data-testid="buy-product-detail-name">{product.name}</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
            aria-label="Close"
            data-testid="buy-product-detail-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {src && (
          <div className="relative bg-muted/40 flex items-center justify-center" style={{ height: 320 }}>
            <img src={src} alt={product.name} className={`max-h-full max-w-full ${fitClass}`} />
            {product.badge_text && (
              <ProductBadge
                text={product.badge_text}
                position={product.badge_position}
                bgColor={product.badge_bg_color}
                textColor={product.badge_text_color}
                fontFamily={product.badge_font_family}
                fontSize={product.badge_font_size}
                rotation={product.badge_rotation}
                testId="buy-product-detail-badge"
              />
            )}
            {product.purchased && <PurchasedRibbon date={fmtDate(product.purchased_at)} />}
          </div>
        )}
        <div className="p-5 space-y-4">
          {product.short_description && (
            <p className="text-sm text-muted-foreground" data-testid="buy-product-detail-short-desc">
              {product.short_description}
            </p>
          )}
          {product.supplier_name && (
            <p className="text-xs text-muted-foreground" data-testid="buy-product-detail-supplier">
              Supplied by <span className="font-medium">{product.supplier_name}</span>
            </p>
          )}
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-serif text-2xl" data-testid="buy-product-detail-price">
              {formatIntervalPrice(product, billingInterval)}
            </span>
            {billingInterval === "one_off" && Number(product.was_price || 0) > Number(product.price || 0) && (
              <span className="text-sm text-muted-foreground line-through">
                {symbolFor(product.currency)}{Number(product.was_price).toFixed(2)}
              </span>
            )}
            {!product.purchased && (
              <BillingIntervalToggle product={product} value={billingInterval} onChange={setBillingInterval} testIdPrefix="buy-product-detail-interval" />
            )}
          </div>
          {product.description && (
            <RichHtml
              className="prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline"
              data-testid="buy-product-detail-description"
              html={product.description}
            />
          )}
        </div>
        <div className="border-t px-5 py-3 flex items-center justify-end">
          {product.purchased ? (
            <span
              className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded bg-emerald-100 text-emerald-700"
              data-testid="buy-product-detail-purchased"
            >
              <CheckCircle2 className="h-4 w-4" />
              {product.purchased_at ? `Purchased on ${fmtDate(product.purchased_at)}` : "Purchased"}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onBuy(billingInterval)}
              disabled={isBuying}
              className="text-sm font-medium px-4 py-2 rounded bg-primary text-primary-foreground hover:opacity-90 transition-colors inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="buy-product-detail-buy-btn"
            >
              {isBuying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              Buy Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
